import { useEffect, useRef, useState } from 'react';

import {
  calculateProgress,
  clampProgress,
  getIsAnyToastHovered,
  getIsToastHovered,
  handleMouseLeave,
  hoverListeners,
  PROGRESS_UPDATE_INTERVAL,
} from './toast.helpers';

interface UseToastProgressParams {
  shouldShowProgress: boolean;
  createdAt: number;
  toastId: string | number;
  duration: number;
}

/**
 * Custom hook for managing toast progress bar
 *
 * Handles:
 * - Progress calculation and updates
 * - Global hover state subscription (syncs with Sonner's pause behavior)
 * - Pause/resume logic when any toast is hovered
 */
export const useToastProgress = ({ shouldShowProgress, createdAt, toastId, duration }: UseToastProgressParams): number => {
  const [progress, setProgress] = useState<number>(100);
  const isPausedRef = useRef<boolean>(false);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const pauseStartTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Progress bar logic - syncs with Sonner's timer
  useEffect(() => {
    if (!shouldShowProgress) {
      return;
    }

    // Validate duration once at the start
    if (duration <= 0 || !Number.isFinite(duration)) {
      return;
    }

    // Initialize with createdAt to sync with Sonner's timer
    startTimeRef.current = createdAt;
    pausedTimeRef.current = 0;
    isPausedRef.current = false;

    setProgress(100);

    const updateProgress = (): void => {
      if (isPausedRef.current) {
        return;
      }

      const progressPercent = calculateProgress(startTimeRef.current, pausedTimeRef.current, duration);

      setProgress(clampProgress(progressPercent));

      if (progressPercent <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);

        intervalRef.current = null;
      }
    };

    // Update progress every 50ms for smooth animation (20fps is enough for progress bar)
    intervalRef.current = setInterval(updateProgress, PROGRESS_UPDATE_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);

        intervalRef.current = null;
      }
    };
  }, [shouldShowProgress, duration, createdAt]);

  // Subscribe to global hover state and handle pause/resume directly
  useEffect(() => {
    if (!shouldShowProgress) {
      return;
    }

    const pause = (): void => {
      if (!isPausedRef.current) {
        pauseStartTimeRef.current = Date.now();
        isPausedRef.current = true;
      }
    };

    const resume = (): void => {
      if (isPausedRef.current) {
        const pauseDuration = Math.max(0, Date.now() - pauseStartTimeRef.current);
        pausedTimeRef.current += pauseDuration;
        isPausedRef.current = false;

        // Immediately update progress on resume (don't wait for next interval tick)
        const progressPercent = calculateProgress(startTimeRef.current, pausedTimeRef.current, duration);

        setProgress(clampProgress(progressPercent));
      }
    };

    // Handler for global hover state changes - delegates to pause/resume
    const handleHoverChange = (isHovered: boolean): void => {
      if (isHovered) {
        pause();

        return;
      }

      resume();
    };

    hoverListeners.add(handleHoverChange);

    // Initialize pause state if any toast is already hovered
    if (getIsAnyToastHovered()) {
      pause();
    }

    return () => {
      hoverListeners.delete(handleHoverChange);
      // Cleanup hover state on unmount (only if toast was actually hovered)
      if (getIsToastHovered(toastId)) {
        handleMouseLeave(toastId);
      }
    };
  }, [shouldShowProgress, duration, toastId]);

  return progress;
};
